import type { Home, PerAssessmentRow } from '@lmc/ts-common'
import { Avatar } from '@lmc/ui-kit'
import { Table, type TableColumnGroupType } from 'antd'
import { getNominative } from 'common/utils'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import {
  BaseText,
  SpacedColumnHeader,
  YearContainer,
  YearText
} from 'screens/Reports/RiskAssessmentDashboard/components/CommonStyledComponents'
import RiskAssessmentCell from 'screens/Reports/RiskAssessmentDashboard/components/RiskAssessmentCell/RiskAssessmentCell'
import { generateYearMonthsArray } from 'screens/Reports/RiskAssessmentDashboard/screens/utils/utils'
import styled from 'styled-components'

interface DashboardTableProps {
  dataSource?: PerAssessmentRow[]
  to: dayjs.Dayjs
  from: dayjs.Dayjs
}

const DashboardTable = ({ dataSource, to, from }: DashboardTableProps) => {
  const yearMonths = generateYearMonthsArray(from, to)

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const columns: TableColumnGroupType<PerAssessmentRow>[] = [
    {
      title: ' ',
      children: [
        {
          title: (
            <SpacedColumnHeader>
              <BaseText weight="medium">{peopleNominative}</BaseText>
            </SpacedColumnHeader>
          ),
          dataIndex: 'resident',
          key: 'resident',
          fixed: 'left',
          sorter: (a: PerAssessmentRow, b: PerAssessmentRow) =>
            a.resident.name?.first?.localeCompare(b.resident?.name?.first),
          defaultSortOrder: 'ascend',
          render: (row: PerAssessmentRow['resident']) => (
            <PersonCard>
              <Avatar url={row.picture} size={30} />
              <BaseText weight="bold">
                {row.name?.first} {row.name?.last}
              </BaseText>
            </PersonCard>
          )
        }
      ]
    },
    ...yearMonths.map((yearMonth) => ({
      title: (
        <YearContainer>
          <YearText level={4} weight="regular">
            {yearMonth.year}
          </YearText>
        </YearContainer>
      ),
      children: yearMonth.months.map((month) => ({
        dataIndex: 'months',
        title: (
          <BaseText weight="medium">
            {dayjs(month, 'MM/YYYY').format('MMM')}
          </BaseText>
        ),
        key: `assessments-${month}`,
        render: (row: PerAssessmentRow['months']) => (
          <RiskAssessmentCell row={row} month={month} />
        )
      }))
    }))
  ]

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      scroll={{ x: 'max-content' }}
      bordered
      pagination={{
        showSizeChanger: true,
        position: ['bottomCenter']
      }}
    />
  )
}

const PersonCard = styled.div`
  width: 220px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 100px;
  }
`

export default DashboardTable
